import React from 'react';

import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import Tooltip from '~/components/core/Atomic/Tooltip';
import { ChevronDownIcon } from '~/components/icons';
import Radio from '~/components/ui/atoms/Radio';
import cn from '~/Utils/cn';

interface ConflictOptionCardProps {
  /** Shared by the two options of one conflict, so they form a single radio group. */
  name: string;
  selected: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
  title: string;
  subtitle: string;
  description: string;
  resultTitle: React.ReactNode;
  onSelect: () => void;
  children: React.ReactNode;
}

/** One of the two resolutions for a conflicting object, as a selectable card: the radio and title, a
 * one-line explanation, and the Result the choice produces across every field the decision touches. Both
 * outcomes sit side by side so the approver compares them, rather than previewing one at a time through a
 * dropdown. A click anywhere on the card selects it; the radio stays the accessible control. */
const ConflictOptionCard = ({
  name,
  selected,
  disabled = false,
  disabledTooltip,
  title,
  subtitle,
  description,
  resultTitle,
  onSelect,
  children,
}: ConflictOptionCardProps): React.ReactNode => {
  const card = (
    <div
      onClick={disabled ? undefined : onSelect}
      className={cn(
        'flex flex-col gap-y-4 rounded-md border border-solid bg-white pb-8 pe-8 pt-2 text-sm',
        selected ? 'border-teal-600' : 'border-slate-500',
        disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'
      )}
    >
      <div className="flex items-center gap-x-4">
        <label
          className="cursor-[inherit]"
          onClick={(event) => {
            // prevent a double invocation of the `onSelect` handler when the click target is the accessible control
            event.stopPropagation();
          }}
        >
          <Radio size="xs" name={name} checked={selected} disabled={disabled} onChange={onSelect} />
          <span className="text-base font-medium">{title}</span>
          <span className="text-slate-800"> – {subtitle}</span>
        </label>
      </div>
      <div className={cn('pl-32', selected ? 'text-teal-700' : 'text-slate-800')}>{description}</div>
      <div className="flex flex-col gap-y-2 pl-32">
        <div className="flex items-center font-medium">{resultTitle}</div>
        {children}
      </div>
    </div>
  );
  if (!disabledTooltip) {
    return card;
  }
  // A disabled radio sets pointer-events:none, so the tooltip hovers on a wrapping span instead.
  return (
    <Tooltip title={disabledTooltip}>
      <span>{card}</span>
    </Tooltip>
  );
};

export const ResultTitleWithExpandToggle: React.FC<{
  expanded: boolean;
  onToggleExpanded: () => void;
  /** What the toggle opens, for its accessible name — "full result" by default; a toggle that drives a
   * shared state (the reorder card opens both orders at once) must say so, or the untouched sibling's
   * `aria-expanded` misreports what it owns. */
  toggleLabel?: string;
  children: React.ReactNode;
}> = ({ expanded, onToggleExpanded, toggleLabel = 'full result', children }) => {
  // prevent option selection when toggling the order list
  // and prevent a double invocation when click target is the accessible button
  const onToggleNoPropagation = (event: React.MouseEvent) => {
    event.stopPropagation();
    onToggleExpanded();
  };
  return (
    <div className="w-fit font-medium" onClick={onToggleNoPropagation}>
      {children}
      <IconButton
        onClick={onToggleNoPropagation}
        className="ms-4"
        aria-expanded={expanded}
        aria-label={`${expanded ? 'Collapse' : 'Expand'} ${toggleLabel}`}
      >
        <ChevronDownIcon className={cn({ 'rotate-180': expanded })} iconColor="currentColor" size={16} />
      </IconButton>
    </div>
  );
};

export default ConflictOptionCard;
